"use client";

import React from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  Grid,
  Card,
  CardHeader,
  CardBody,
  Text,
  VStack,
  IconButton,
  Badge,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { DeleteIcon, EditIcon, DownloadIcon } from "@chakra-ui/icons";
import { FileText, BookPlus, Book } from "lucide-react";
import { motion } from "framer-motion";
import EmailAgents from "../EmailAgents";
import EmailAddresses from "./EmailAddresses";
import {
  emailKnowledgeBaseService,
  type EmailKnowledgeBase,
} from "../../email-services/knowledgeBaseService";
import { AddKnowledgeBaseModal } from "../email-knowledge-base/AddKnowledgeBaseModal";
import { KnowledgeBaseDetailsModal } from "../email-knowledge-base/KnowledgeBaseDetailsModal";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";
import type { EmailKnowledgeBaseSource } from "../../email-services/knowledgeBaseService";

const MotionBox = motion(Box);

// Match the VoiceAgents optimization style
const CACHE_TTL_MS = 5 * 60 * 1000;

const EmailKBComponent = () => {
  const [selectedEmailTab, setSelectedEmailTab] =
    React.useState<string>("knowledge-base");
  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "Email";
    }
  );

  const toast = useToast();
  const navigate = useNavigate();

  const [knowledgeBases, setKnowledgeBases] = React.useState<
    EmailKnowledgeBase[] | null
  >(null);
  const [selectedKB, setSelectedKB] = React.useState<EmailKnowledgeBase | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState(true);

  // Optional: track downloads, so UI stays responsive and doesn't duplicate clicks
  const [downloading, setDownloading] = React.useState<Record<string, boolean>>(
    {}
  );

  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();

  const { isAdmin, currentClientId, clients } = useAuth();
  const credits = useStableCredits();

  const abortControllerRef = React.useRef<AbortController | null>(null);

  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient ? currentClient.company_name : undefined;
  }, [isAdmin, currentClientId, clients]);

  const getCacheKey = React.useCallback(
    (key: string) => `email_kb_${key}_${companyName || "self"}`,
    [companyName]
  );

  const loadFromCache = React.useCallback(
    (key: string) => {
      try {
        const raw = localStorage.getItem(getCacheKey(key));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed.timestamp || 0);
        if (age > CACHE_TTL_MS) return null;
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [getCacheKey]
  );

  const saveToCache = React.useCallback(
    (key: string, data: any) => {
      try {
        localStorage.setItem(
          getCacheKey(key),
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch {}
    },
    [getCacheKey]
  );

  // --- tab + agent type listeners (kept as-is, but guarded) ---
  React.useEffect(() => {
    const savedTab =
      localStorage.getItem("currentAgentTab") || "knowledge-base";
    setSelectedEmailTab(savedTab);

    const handleTabChange = (event: any) => {
      setSelectedEmailTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    const handleAgentTypeChangeEvent = (event: any) => {
      setSelectedAgentType(event.detail);
    };

    window.addEventListener("agentTabChange", handleTabChange);
    window.addEventListener("agentTypeChange", handleAgentTypeChangeEvent);
    return () => {
      window.removeEventListener("agentTabChange", handleTabChange);
      window.removeEventListener("agentTypeChange", handleAgentTypeChangeEvent);
    };
  }, []);

  // ✅ Tenant switch behavior: abort in-flight, clear previous tenant UI, and show loader only if no cache
  React.useEffect(() => {
    // Abort any in-flight request from previous tenant
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear previous tenant UI state (prevents flashing wrong company KBs)
    setKnowledgeBases(null);
    setSelectedKB(null);
    setDownloading({});

    // Only show the big loader if we have nothing cached for this tenant
    const hasCachedKbs = !!loadFromCache("knowledge_bases");
    setIsLoading(!hasCachedKbs);
  }, [companyName, loadFromCache]);

  // --- optimized fetch with cache-first hydration + abort safety ---
  const fetchKnowledgeBases = React.useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 1) Hydrate from cache immediately for instant UI
    const cachedKbs = loadFromCache("knowledge_bases");
    if (cachedKbs) {
      setKnowledgeBases(cachedKbs);
      // If cache exists, do not force big loader; we'll silently refresh
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    try {
      // 2) Fetch fresh
      // NOTE: service may not support AbortSignal. We still guard state updates by signal.aborted.
      const kbs = await emailKnowledgeBaseService.list(companyName);

      if (signal.aborted) return;

      setKnowledgeBases(kbs);
      saveToCache("knowledge_bases", kbs);
    } catch (error: any) {
      if (error?.name === "AbortError") return;

      console.error("[v0] Error fetching knowledge bases:", error);
      toast({
        title: "Error fetching knowledge bases",
        status: "error",
        duration: 3000,
      });

      if (!signal.aborted) {
        setKnowledgeBases([]);
        saveToCache("knowledge_bases", []);
      }
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, [companyName, loadFromCache, saveToCache, toast]);

  // Fetch on tenant change
  React.useEffect(() => {
    fetchKnowledgeBases();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchKnowledgeBases]);

  const handleTabChange = (newTab: string) => {
    setSelectedEmailTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const handleCreateKnowledgeBase = () => {
    onAddModalOpen();
  };

  const handleCardClick = (kb: EmailKnowledgeBase) => {
    setSelectedKB(kb);
    onDetailsModalOpen();
  };

  const handleDeleteKB = async (kbId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm("Are you sure you want to delete this knowledge base?")
    ) {
      return;
    }

    try {
      await emailKnowledgeBaseService.delete(kbId);
      toast({
        title: "Knowledge base deleted",
        status: "success",
        duration: 3000,
      });

      // Optimistic update + cache update for instant UI, then revalidate
      setKnowledgeBases((prev) => {
        const next = (prev || []).filter((kb) => kb.knowledge_base_id !== kbId);
        saveToCache("knowledge_bases", next);
        return next;
      });

      fetchKnowledgeBases();
    } catch (error) {
      console.error("[v0] Error deleting knowledge base:", error);
      toast({
        title: "Error deleting knowledge base",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleRefreshKB = (updatedKB?: EmailKnowledgeBase) => {
    if (updatedKB) {
      setSelectedKB(updatedKB);
      setKnowledgeBases((prev) => {
        const next =
          prev?.map((kb) =>
            kb.knowledge_base_id === updatedKB.knowledge_base_id
              ? updatedKB
              : kb
          ) || null;

        if (next) saveToCache("knowledge_bases", next);
        return next;
      });
    } else {
      fetchKnowledgeBases();
    }
  };

  const getTabIndex = () => {
    switch (selectedEmailTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 2;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    handleTabChange(tabs[index]);
  };

  const handleDownloadSource = async (
    kbId: string,
    sourceId: number,
    filename: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    const downloadKey = `${kbId}:${sourceId}`;
    setDownloading((prev) => ({ ...prev, [downloadKey]: true }));

    try {
      await emailKnowledgeBaseService.downloadSource(kbId, sourceId);
      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error("[v0] Error downloading source:", error);
      toast({
        title: "Error downloading source",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDownloading((prev) => ({ ...prev, [downloadKey]: false }));
    }
  };

  // Keep original routing logic untouched
  if (selectedAgentType === "Email") {
    if (selectedEmailTab === "agents") {
      return <EmailAgents />;
    }
    if (selectedEmailTab === "phone-numbers") {
      return <EmailAddresses />;
    }
  }

  // Loader behavior: show only when truly loading with no cached UI
  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" w="100%">
        <Spinner size="xl" color="orange.500" />
      </Flex>
    );
  }

  const totalSources =
    knowledgeBases?.reduce((sum, kb) => sum + (kb.sources?.length || 0), 0) ||
    0;
  const completedKBs =
    knowledgeBases?.filter((kb) => kb.status === "complete").length || 0;
  const inProgressKBs =
    knowledgeBases?.filter((kb) => kb.status === "in_progress").length || 0;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedEmailTab === "agents" ? "white" : "gray.200",
                }}
              >
                📧 Email Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedEmailTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📬 Email Addresses
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedEmailTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>
          <Button
            onClick={handleCreateKnowledgeBase}
            variant="brandGradient"
            fontWeight="medium"
            leftIcon={<BookPlus size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to create knowledge base"
                : undefined
            }
          >
            Create Knowledge Base
          </Button>
        </HStack>
      </Flex>

      {knowledgeBases && knowledgeBases.length > 0 && (
        <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Total Knowledge Bases
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="black">
                  {knowledgeBases?.length || 0}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Completed
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {completedKBs}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  In Progress
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="yellow.500">
                  {inProgressKBs}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Total Sources
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {totalSources}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Grid>
      )}

      {!knowledgeBases || knowledgeBases.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Book size={48} color="gray" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
            No Knowledge Bases Found!
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Create a knowledge base to get started.
          </Text>
          <Button
            onClick={handleCreateKnowledgeBase}
            mt={6}
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            leftIcon={<BookPlus size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to create knowledge base"
                : undefined
            }
          >
            Create Knowledge Base
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6}>
          {knowledgeBases.map((kb) => {
            const sources = kb.sources || [];
            const documentCount = sources.filter(
              (s: EmailKnowledgeBaseSource) =>
                s.source_type === "pdf" || s.source_type === "docx"
            ).length;
            const textCount = sources.filter(
              (s: EmailKnowledgeBaseSource) => s.source_type === "txt"
            ).length;

            return (
              <Card
                key={kb.knowledge_base_id}
                borderLeft="4px"
                borderLeftColor="orange.500"
                cursor={
                  credits !== null && credits < 0.01 ? "not-allowed" : "pointer"
                }
                onClick={() => {
                  if (credits !== null && credits < 0.01) return;
                  handleCardClick(kb);
                }}
                _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
                height="250px"
                display="flex"
                flexDirection="column"
                opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
                title={
                  credits !== null && credits < 0.01
                    ? "Insufficient balance to view knowledge base details"
                    : undefined
                }
              >
                <CardHeader flexShrink={0}>
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Text fontWeight="bold" fontSize="lg" color="black">
                          {kb.knowledge_base_name}
                        </Text>
                        <Badge
                          colorScheme={
                            kb.status === "complete"
                              ? "green"
                              : kb.status === "error"
                              ? "red"
                              : "yellow"
                          }
                        >
                          {kb.status}
                        </Badge>
                      </HStack>
                    </VStack>
                    <HStack onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        aria-label="Edit knowledge base"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(kb);
                        }}
                        isDisabled={credits !== null && credits < 0.01}
                        title={
                          credits !== null && credits < 0.01
                            ? "Insufficient balance to edit knowledge base"
                            : undefined
                        }
                      />
                      <IconButton
                        aria-label="Delete knowledge base"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => handleDeleteKB(kb.knowledge_base_id, e)}
                      />
                    </HStack>
                  </Flex>
                </CardHeader>
                <CardBody pt={0} overflowY="auto" flex="1">
                  <VStack spacing={3} align="stretch">
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                      <Box>
                        <HStack spacing={1}>
                          <FileText size={14} color="gray" />
                          <Text fontSize="sm" color="gray.600">
                            Documents
                          </Text>
                        </HStack>
                        <Text fontWeight="bold" color="black">
                          {documentCount}
                        </Text>
                      </Box>
                      <Box>
                        <HStack spacing={1}>
                          <FileText size={14} color="gray" />
                          <Text fontSize="sm" color="gray.600">
                            Texts
                          </Text>
                        </HStack>
                        <Text fontWeight="bold" color="black">
                          {textCount}
                        </Text>
                      </Box>
                    </Grid>

                    <Box bg="gray.50" p={3} borderRadius="md">
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.700"
                        mb={2}
                      >
                        Sources ({sources.length}):
                      </Text>
                      {sources.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          {sources.map(
                            (source: EmailKnowledgeBaseSource, idx: number) => {
                              const downloadKey = `${kb.knowledge_base_id}:${source.id}`;
                              const isDownloading = !!downloading[downloadKey];

                              return (
                                <Flex
                                  key={idx}
                                  justify="space-between"
                                  align="center"
                                  p={2}
                                  bg="white"
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <VStack
                                    align="start"
                                    spacing={0}
                                    flex="1"
                                    minW={0}
                                  >
                                    <Text
                                      fontSize="sm"
                                      fontWeight="medium"
                                      color="black"
                                      noOfLines={1}
                                    >
                                      {source.original_filename || "Untitled"}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {source.source_type}
                                    </Text>
                                  </VStack>
                                  <IconButton
                                    aria-label="Download source"
                                    icon={
                                      isDownloading ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <DownloadIcon />
                                      )
                                    }
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={(e) =>
                                      handleDownloadSource(
                                        kb.knowledge_base_id,
                                        source.id,
                                        source.original_filename || "download",
                                        e
                                      )
                                    }
                                    isDisabled={isDownloading}
                                    flexShrink={0}
                                  />
                                </Flex>
                              );
                            }
                          )}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No sources added yet
                        </Text>
                      )}
                    </Box>

                    {kb.updated_at && (
                      <Text fontSize="xs" color="gray.600" textAlign="right">
                        Last updated:{" "}
                        {new Date(kb.updated_at).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      <AddKnowledgeBaseModal
        isOpen={isAddModalOpen}
        onClose={onAddModalClose}
        onSuccess={() => {
          onAddModalClose();
          fetchKnowledgeBases();
        }}
      />

      <KnowledgeBaseDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={onDetailsModalClose}
        knowledgeBase={selectedKB}
        onRefresh={handleRefreshKB}
      />
    </Box>
  );
};

export default EmailKBComponent;
